import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { loginSchema, type LoginInput } from '@rutatec/shared-schemas'
import { useAuthStore, getPortalPath } from '@/store/auth.store'
import { api } from '@/lib/api'

export const LoginPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { setTokens, setUser } = useAuthStore()
  const unauthorized  = (location.state as { unauthorized?: boolean })?.unauthorized
  const sessionExpired = searchParams.get('expired') === '1'

  const { register, handleSubmit, formState: { errors } } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  })

  const loginMutation = useMutation({
    mutationFn: async (data: LoginInput) => {
      const res = await api.post('/auth/login', data)
      return res.data
    },
    onSuccess: (data) => {
      setTokens(data.accessToken) // refreshToken agora em cookie httpOnly
      setUser(data.user)
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname
      navigate(from || getPortalPath(data.user.role), { replace: true })
    },
  })

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-dark">
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="mb-8 text-center">
          <h1 className="font-display text-5xl font-bold tracking-wider">
            <span className="text-white">RUTA</span>
            <span className="text-red">TEC</span>
          </h1>
          <p className="mt-3 font-body text-sm italic text-white/70 border-t border-b border-white/10 py-3 px-2 leading-relaxed">
            "La plataforma que <strong className="text-white not-italic">conecta el Paraguay que produce</strong>{' '}
            con el <strong className="text-white not-italic">Paraguay que entrega</strong>."
          </p>
        </div>

        {/* Card */}
        <div className="rounded-xl bg-white p-8 shadow-modal">
          <h2 className="mb-6 font-display text-2xl font-bold text-navy">
            Iniciar sesión
          </h2>

          {sessionExpired && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="mt-0.5 shrink-0 text-amber-600">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              Tu sesión expiró. Por seguridad, inicia sesión nuevamente para continuar.
            </div>
          )}

          {unauthorized && (
            <div className="mb-4 rounded-lg bg-red-light border border-red/20 p-3 text-sm text-red">
              No tienes acceso a esa sección.
            </div>
          )}

          {loginMutation.isError && (
            <div className="mb-4 rounded-lg bg-red-light border border-red/20 p-3 text-sm text-red">
              Email o contraseña incorrectos.
            </div>
          )}

          <form onSubmit={handleSubmit((data) => loginMutation.mutate(data))} className="space-y-4" noValidate>
            <div>
              <label htmlFor="email" className="mb-1 block font-body text-sm font-medium text-gray-dark">
                Email
              </label>
              <input
                {...register('email')}
                id="email"
                type="email"
                autoComplete="email"
                className="w-full rounded-lg border border-gray-silver px-4 py-2.5 font-body text-sm
                           focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
                placeholder="correo@empresa.com.py"
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="mb-1 block font-body text-sm font-medium text-gray-dark">
                Contraseña
              </label>
              <input
                {...register('password')}
                id="password"
                type="password"
                autoComplete="current-password"
                className="w-full rounded-lg border border-gray-silver px-4 py-2.5 font-body text-sm
                           focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
              />
              {errors.password && (
                <p className="mt-1 text-xs text-red">{errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center justify-end">
              <a href="/forgot-password" className="font-body text-sm text-navy hover:underline">
                ¿Olvidaste tu contraseña?
              </a>
            </div>

            <button
              type="submit"
              disabled={loginMutation.isPending}
              className="w-full rounded-lg bg-green py-3 font-body font-semibold text-white
                         hover:bg-green-700 disabled:opacity-60 transition-colors"
            >
              {loginMutation.isPending ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Iniciando...
                </span>
              ) : (
                'Iniciar sesión'
              )}
            </button>
          </form>

          <p className="mt-6 text-center font-body text-sm text-gray-DEFAULT">
            ¿No tienes cuenta?{' '}
            <a href="/register" className="font-semibold text-navy hover:underline">
              Registrarse
            </a>
          </p>
        </div>

        <p className="mt-6 text-center font-body text-xs text-white/40">
          © 2025 RUTATEC · Todos los derechos reservados
        </p>
      </div>
    </div>
  )
}
